import * as R from 'ramda';
import { isNilOrEmpty } from './module';
import { addMinutes, format, formatISO, isValid, parseISO } from 'date-fns';


export const UNIX_EPOCH_STR = '1970-01-01T00:00:00.000Z';


export const UNIX_EPOCH = new Date(UNIX_EPOCH_STR);

/**
 * Formats a date or a date-encoded string into a date string that can be displayed to the user.
 * Returns undefined if the date param is not a date.
 * @param date 
 * @param dateFormat 
 * @returns 
 */
export const formatDate = (date: string | Date, dateFormat: string): string | undefined => {
  if (isNilOrEmpty(date)) {
    return undefined;
  }
  const d = new Date(date);

  return format(d, dateFormat);
};

/**
 * Formats a date or a date-encoded string into a time string that can be displayed to the user.
 * Returns undefined if the date param is not a date.
 */
export const formatTime = (date: string | Date, timeFormat: string): string | undefined => {
  if (isNilOrEmpty(date)) {
    return undefined;
  }
  const d = new Date(date);

  return format(d, timeFormat);
}; 


/**
 * Formats a TimeOnly string or a date-encoded string into a time string 
 * that can be displayed to the user.
 * 
 * shortVersion = true:
 *   - If minutes === 00 → output only hour (e.g., "8 AM")
 *   - If minutes > 0 → output based on the specified time format
 *
 * If shortVersion = false:
 *   - Uses the provided timeFormat exactly
 *
 * Returns undefined for invalid input.
 */
export const formatTimeOnly = (
  time: string | Date | null | undefined,
  timeFormat: string,
  shortVersion: boolean = false
): string | undefined => {

  if (isNilOrEmpty(time)) {
    return undefined;
  }

  if (time instanceof Date) {
    return shortVersion
      ? formatShort(time, timeFormat)
      : format(time, timeFormat);
  }

  // If time-only (e.g., "08:30:00"), build a valid Date
  const fullDate = `1970-01-01T${time}`;
  const d = new Date(fullDate);

  if (isNaN(d.getTime())) {
    return undefined;
  }

  return shortVersion
    ? formatShort(d, timeFormat)
    : format(d, timeFormat);
};

/**
 * - Removes minutes if they are "00"
 * - Otherwise returns hour:minute
 */
const formatShort = (date: Date, timeFormat: string): string => {
  const minutes = date.getMinutes();

  // Minutes are zero → show hour only (e.g., "8 AM")
  if (minutes === 0) {
    return format(date, "h a");
  }

  return format(date, timeFormat);
};


/**
 * Combines formatDate and formatTime into one singular function returning a "MM/DD/YYYY hh:mm:AMPM" format.
 * For display to the user
 */
export const formatDateAndTime = (date: string | Date, dateFormat: string, timeFormat: string): string | undefined => {
  if (isNilOrEmpty(date)) {
    return undefined;
  }

  return `${formatDate(date, dateFormat)} ${formatTime(date, timeFormat)}`;
};

/**
 * Converts a local string date to ISO UTC date/time string
 * example: 2025-05-30 => 2025-05-30T06:00:00-06:00
 */
export const convertToUTCString = (localDate: string) => {
  const date = parseISO(localDate);
  if (!isValid(date)) {
    return undefined;
  }

  const offset = date.getTimezoneOffset();
  const utcDate = addMinutes(date, offset);
  return formatISO(utcDate);
};

/**
 * Calculates how many years ago the given date was from today
 */
export const calcAge = (date: string | Date): number => {
  const diffMs = Date.now() - new Date(date).getTime();

  /* Epoch starts on Jan 1, 1970. Normally, when doing new Date(diffMs), a positive time will count the duration of
   1970 in determining the date. When the date provided is in the future, we will have a negative diffMs. Going
   backwards, instead of having the full 1970 to go through, since we're starting on Jan 1, 1970, even one day back
   is technically a year difference by the math in our return.
   */
  const dateInFuture = diffMs < 0;
  return new Date(diffMs).getUTCFullYear() - (dateInFuture ? 1969 : 1970);
};


/**
 * Returns true if the given date calculates out to greater than or equal to the given age.
 * @param ageToCompare - The threshold age (usually 18)
 * @param date - The birth date of the person whose age you want to compare
 */
export const isOlderThan = (ageToCompare: number, date: string | Date): boolean => calcAge(date) >= ageToCompare;


/**
 * Returns true if the given ski season string is the current ski season.
 * @param seasonStr - These strings look like "2018-2019".  undefined, empty, or nonsensical strings return false.
 * Uses today's date to see if season string is a match with the current season.
 */
export const isCurrentSeason = (seasonStr: string | undefined): boolean => dateMatchesSeason(new Date(), seasonStr);


/**
 * Returns whether or not the given ski season string is the ski season of the date given. These strings look like "2018-2019"
 * If you pass undefined or an empty string, you'll get a false back -- because undefined is not the season of the date given.
 * Heuristically checks that the string is the right length, but will otherwise explode if e.g. you pass "abcd-efgh"
 */
export const dateMatchesSeason = (date: Date | string | undefined, seasonStr: string | undefined): boolean => {
  if (!seasonStr || seasonStr.length !== 9 || !date) {
    return false;
  }

  const d           = new Date(date);
  const yearOne     = +seasonStr.substr(0, 4);
  const yearTwo     = +seasonStr.substr(5, 4);
  const currentYear = d.getFullYear();

  if (currentYear !== yearOne && currentYear !== yearTwo) {
    return false;
  }

  const currentMonth = d.getMonth() + 1; // 1 - 12

  // We're in this season if the current year is late yearOne, or early yearTwo
  return (currentYear === yearOne && currentMonth > 6)     // Late year one
         || (currentYear === yearTwo && currentMonth < 6); // Early year two
};


/**
 * Returns the number of seconds that have elapsed since the given date. If the input is undefined, then uses now. If
 * the given date is in the future, returns a negative number -- "it's been -10s since 10s from now"
 */
export const secondsSince = (
  referenceTime: Date | string,
): number => (new Date().getTime() - new Date(referenceTime || new Date()).getTime()) / 1000;


/**
 * Similar to secondsSince, except inverted. Returns the number of seconds until the given date time. If the given
 * date time is in the past, returns a negative number.
 */
export const secondsUntil = (
  referenceTime: Date | string,
): number => -1 * secondsSince(referenceTime);


/**
 * Returns the number of milliseconds that have elapsed since the given date. If the input is undefined, then uses now.
 * If the given date is in the future, returns a negative number -- "it's been -10ms since 10ms from now"
 */
export const millisecondsSince = (
  referenceTime: Date | string,
): number => (new Date().getTime() - new Date(referenceTime || new Date()).getTime());


/**
 * Similar to millisecondsSince, except inverted. Returns the number of milliseconds until the given date time. If the
 * given date time is in the past, returns a negative number.
 */
export const millisecondsUntil = (
  referenceTime: Date | string,
): number => -1 * millisecondsSince(referenceTime);


/**
 * Converts the given date to a local time string format that the server will understand. This is
 * @param date - the date to convert
 */
export const toServerLocalTime = (date: Date | string) => {
  const d = new Date(date);

  const month = String(d.getMonth() + 1);
  const day   = String(d.getDate());
  const year  = String(d.getFullYear());

  const hours   = String(d.getHours());
  const minutes = String(d.getMinutes());
  const seconds = String(d.getSeconds());
  const ms      = String(d.getMilliseconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms}`;
};


/**
 * Converts the given date to a date string format with 00:00:00 for the time. 
 *  YYYY-MM-DDT00:00:00
 * @param date - the date to convert
 */
export const toDateOnlyString = (date: Date | string) => {
  const d = new Date(date);

  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day   = String(d.getDate()).padStart(2, '0');
  const year  = String(d.getFullYear());

  return `${year}-${month}-${day}T00:00:00`;
};

/**
 * Converts the given date to its first second, local time with no offset.
 *  YYYY-MM-DDT00:00:00
 */
export const toStartOfDayString = (date: Date | string) => toDateOnlyString(date);

/**
 * Converts the given date to its last second, local time with no offset.
 *  YYYY-MM-DDT23:59:59
 */
export const toEndOfDayString = (date: Date | string) =>
  `${toDateOnlyString(date).slice(0, 10)}T23:59:59`;

/**
 * Gets the number of milliseconds between the two dates. Does not return absolute magnitude, so if your first date is
 * less than your second date, you'll get back a negative number.
 */
export const msBetween = (
  date1: Date | string,
  date2: Date | string,
): number => new Date(date1).getTime() - new Date(date2).getTime();

/**
 * Converts four-digit year ranges ("2019-2020" or "2019 - 2020") into two-digit year ranges ("19-20") often used
 * by passes on the UI side.
 * @param yearRangeString - A string with four-digit year ranges ("2019-2020" or "2019 - 2020")
 */
export const toTwoDigitYearRanges = (yearRangeString: string): string => {
  if (!yearRangeString.match(/\d{4}\s?-\s?\d{4}$/)) {
    return yearRangeString;
  }

  const years  = yearRangeString.replace(' ', '')
                                .split('-');

  return R.takeLast(2, years[ 0 ]) + '-' + R.takeLast(2, years[ 1 ]);
}


/**
 * Converts the given to a date with the 00:00 time.
 * @param date - the date to convert
 */
export const toDateOnly = (date: string | Date): Date | undefined => {
  if (isNilOrEmpty(date)) {
    return undefined;
  }

  const dateFromData = new Date(date);
  if (!dateFromData) {
    return undefined;
  }
  
  return new Date(dateFromData.getFullYear(), dateFromData.getMonth(), dateFromData.getDate());
};

/**
 * Converts the given to a date string with the time of day stripped out. This is needed for certain API endpoints, like
 * the entitlement-based hotlist add endpoint.
 * @param date - the date to convert
 */
export const toLocalDateAsString = (date: string | Date): string | undefined => {
  if (isNilOrEmpty(date)) {
    return undefined;
  }

  const dateFromData = new Date(date);
  if (!dateFromData) {
    return undefined;
  }
  
  return formatISO(dateFromData, { representation: 'date'});
};

export const toLocalISOString = (date: string | Date) : string | undefined => {
  if (isNilOrEmpty(date)) {
    return undefined;
  }

  const dateFromData = new Date(date);
  if (!dateFromData) {
    return undefined;
  }

  return new Date(dateFromData.getTime() - (dateFromData.getTimezoneOffset() * 60000)).toISOString()
};


/** Adds the specified number of years to the given date or date string.
 * @param date the date or date string to which years should be added
 * @param years the number of years by which to increment the date
 */
export const addYears = (date: string | Date, years: number): Date | undefined => {
  if (isNilOrEmpty(date)) {
    return undefined;
  }

  const dateFromData = new Date(date);

  dateFromData.setFullYear(dateFromData.getFullYear() + years);
  return dateFromData;
};


/** Formats a date in 12-31-1970 format with non-breaking hyphens
 * @param date The date to format as either a Date or a string parseable by new Date()
 */
export const toUsDateFormat = (givenDate: Date | string | undefined): string => {
  // Non-breaking hyphen so dates aren't broken in the middle
  const nbh = "‑";

  const zeroPad = (shortStr: string) => ('00' + shortStr).slice(-2);

  if (givenDate) {
    const dateObj = new Date(givenDate);
    if (!isNaN(dateObj!.getTime())) {
      const month = zeroPad((dateObj.getMonth() + 1).toString());
      const day = zeroPad(dateObj.getDate().toString());
      return `${month}${nbh}${day}${nbh}${dateObj.getFullYear()}`;
    }
  }

  return '';
} 

/** Parses a date string which is either in UTC (e.g. "2019-11-11T02:42:15.717Z") or is 
 *  implicitly in UTC time but written like a local string (e.g. "2019-11-11T02:42:15.717")
 *  (e.g. it should have a trailing 'Z' but doesn't)
 *  Returns a date object set to that UTC time.  If parsing fails, returns the unix epoch date.
 */
export const convertUtcStringDateToDate = (container: unknown, fieldName: string): Date => {
  const givenDateStr: string = R.pathOr('', [ fieldName ], container);
  const parseDateStr = givenDateStr.slice(-1) === 'Z' ? givenDateStr : givenDateStr + 'Z';
  return new Date(Date.parse(parseDateStr) || UNIX_EPOCH_STR);
}


/** Given a Date or a string representing a date, returns a Date
 */
export const makeDateFrom = (givenDate: string | Date | undefined): Date =>
  new Date(givenDate || '')


/** Given a date to test, and another two dates defining a date range,
 *  returns true if the test date occurs within the range of the other two (inclusive).
 *  Returns false if any of the dates are undefined.
 */
export const isBetweenDates = (
  testDate?: string | Date,
  firstDate?: string | Date,
  secondDate?: string | Date
): boolean => {
  if (!testDate || !firstDate || !secondDate) {
    return false;
  }

  const [ dTestDate, dFirstDate, dSecondDate] = [testDate, firstDate, secondDate].map(makeDateFrom);
  const [ beginDate, endDate ] =  dFirstDate < dSecondDate  ? [ dFirstDate, dSecondDate ] : [ dSecondDate, dFirstDate ];
  return (dTestDate >= beginDate && dTestDate <= endDate);
}

export const isDateDifferenceGreaterThanOneYear = (date1: Date, date2: Date): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  const yearDiff = Math.abs(d2.getFullYear() - d1.getFullYear());

  if (yearDiff > 1) return true;
  if (yearDiff === 1) {
    // Check if month/day makes it a full year
    if (d2.getMonth() > d1.getMonth()) return true;
    if (d2.getMonth() === d1.getMonth() && d2.getDate() >= d1.getDate()) return true;
  }
  return false;
}


// Format a Date to "YYYY-MM-DD" using *local* timezone
export const toLocalISODate = (d: Date): string => {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Parse inputs that may be string or Date into local date-only string for date comparisons
export const normalizeToLocalISO = (input: string | Date): string => {
  if (input instanceof Date) return toLocalISODate(input);
  // If it's already date-only "YYYY-MM-DD", keep it
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;

  // Else parse as Date and normalize
  const parsed = new Date(input);
  if (isNaN(parsed.getTime())) {
    throw new Error(`Invalid date input: ${input}`);
  }
  return toLocalISODate(parsed);
}
